// src/components/asset/ModuleDesigner.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Slider, Typography, Box, Alert, Chip, Divider, Switch,
  FormControlLabel, Autocomplete, InputAdornment, Paper
} from '@mui/material';
import { 
  Save as SaveIcon, 
  AutoAwesome as MagicIcon, 
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

const PURPOSES = [
  { value: 'habitation', label: 'Жилой отсек', category: 'civilian', icon: '🏠' },
  { value: 'energy', label: 'Энергетика', category: 'industrial', icon: '⚡' },
  { value: 'energy_storage', label: 'Накопители энергии', category: 'industrial', icon: '🔋' },
  { value: 'propulsion', label: 'Двигатели', category: 'industrial', icon: '🚀' },
  { value: 'weapon_system', label: 'Вооружение', category: 'military', icon: '⚔️' },
  { value: 'shield_generator', label: 'Генераторы щита', category: 'military', icon: '🛡️' },
  { value: 'armor', label: 'Броня', category: 'military', icon: '🛡️' },
  { value: 'sensor', label: 'Сенсоры', category: 'scientific', icon: '📡' },
  { value: 'cargo', label: 'Грузовой отсек', category: 'civilian', icon: '📦' },
  { value: 'life_support', label: 'Жизнеобеспечение', category: 'civilian', icon: '🌱' },
  { value: 'command', label: 'Управление', category: 'civilian', icon: '🎮' },
  { value: 'docking_bay', label: 'Стыковочный узел', category: 'civilian', icon: '🔌' },
  { value: 'research', label: 'Лаборатория', category: 'scientific', icon: '🔬' },
  { value: 'medical', label: 'Медблок', category: 'civilian', icon: '🏥' },
  { value: 'manufacturing', label: 'Производство', category: 'industrial', icon: '🏭' },
  { value: 'mining', label: 'Добыча ресурсов', category: 'industrial', icon: '⛏️' },
  { value: 'agriculture', label: 'Сельское хозяйство', category: 'civilian', icon: '🌾' },
  { value: 'communication', label: 'Коммуникации', category: 'civilian', icon: '📡' },
  { value: 'navigation', label: 'Навигация', category: 'civilian', icon: '🧭' },
  { value: 'special', label: 'Специальные модули', category: 'special', icon: '✨' },
  { value: 'composite', label: 'Композитные модули', category: 'special', icon: '🔀' },
  { value: 'nanotech', label: 'Нанотехнологии', category: 'scientific', icon: '🧬' },
  { value: 'repair', label: 'Ремонт и обслуживание', category: 'industrial', icon: '🔧' },
  { value: 'structure', label: 'Структурные элементы', category: 'civilian', icon: '🏗️' }
];

const SHAPES = [
  { value: 'cube', label: 'Куб' },
  { value: 'rhombus', label: 'Ромб' },
  { value: 'hexagon', label: 'Шестиугольник' },
  { value: 'triangle_prism', label: 'Треугольная призма' },
  { value: 'cylinder', label: 'Цилиндр' },
  { value: 'sphere', label: 'Сфера' },
  { value: 'dome', label: 'Купол' },
  { value: 'torus', label: 'Тор' }
];

const SIZES = [
  { value: 'nano', label: 'Нано (1x)', multiplier: 0.1 },
  { value: 'micro', label: 'Микро (2x)', multiplier: 0.5 },
  { value: 'small', label: 'Малый (3x)', multiplier: 1 },
  { value: 'medium', label: 'Средний (4x)', multiplier: 2 },
  { value: 'large', label: 'Большой (5x)', multiplier: 5 },
  { value: 'huge', label: 'Огромный (6x)', multiplier: 10 },
  { value: 'capital', label: 'Капитальный (8x)', multiplier: 20 }
];

const CATEGORIES = [
  { value: 'all', label: 'Все категории' },
  { value: 'civilian', label: 'Гражданские' },
  { value: 'military', label: 'Военные' },
  { value: 'industrial', label: 'Промышленные' },
  { value: 'scientific', label: 'Научные' },
  { value: 'special', label: 'Специальные' }
];

const ENVIRONMENTS = [
  { value: 'vacuum', label: 'Вакуум' },
  { value: 'atmosphere', label: 'Атмосфера' },
  { value: 'underwater', label: 'Подводный' },
  { value: 'surface', label: 'Поверхность' }
];

export default function ModuleDesigner({ 
  open, 
  onClose, 
  onSave, 
  initialData, 
  availableModules, 
  mode = 'create',
  sizeTiers,
  techLevels 
}) {
  const [formData, setFormData] = useState({
    name: '',
    purpose: 'habitation',
    shape: 'cube',
    size_tier: 'medium',
    tech_level: 5,
    mass_kg: 1000,
    power_draw: 50,
    power_output: 0,
    crew_capacity: 0,
    description: '',
    is_custom: true,
    compatible_environments: ['vacuum', 'atmosphere'],
    tags: [],
    slot_size: '3x',
    base_stats: {}
  });

  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Фильтрация доступных модулей по категории и поиску
  const filteredModules = React.useMemo(() => {
    if (!availableModules || !Array.isArray(availableModules)) return [];
    
    return availableModules.filter(module => {
      // Фильтр по категории
      if (filterCategory !== 'all') {
        const purposeObj = PURPOSES.find(p => p.value === module.purpose);
        if (purposeObj?.category !== filterCategory) return false;
      }
      
      // Поиск по названию
      if (searchTerm && module.name) {
        return module.name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      return true;
    });
  }, [availableModules, filterCategory, searchTerm]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        // Извлекаем данные из initialData, учитывая структуру из ModuleTemplates
        const baseStats = initialData.base_stats || {};
        
        setFormData({
          name: initialData.name || '',
          purpose: initialData.purpose || 'habitation',
          shape: initialData.shape || initialData.base_shape || 'cube',
          size_tier: initialData.size_tier || 'medium',
          tech_level: initialData.tech_level || initialData.tech_level_min || 5,
          mass_kg: baseStats.mass_kg || initialData.mass_kg || initialData.mass || 1000,
          power_draw: baseStats.power_draw || initialData.power_draw || initialData.consumption_rates?.power || 50,
          power_output: baseStats.power_output || initialData.power_output || 0,
          crew_capacity: baseStats.crew_capacity || initialData.crew_capacity || 0,
          description: initialData.description || '',
          is_custom: initialData.is_custom !== undefined ? initialData.is_custom : true,
          compatible_environments: initialData.compatible_environments || 
                                   (initialData.environment_required ? [initialData.environment_required] : ['vacuum', 'atmosphere']),
          tags: initialData.tags || [],
          slot_size: initialData.slot_size || '3x',
          base_stats: baseStats
        });
      } else {
        setFormData({
          name: '',
          purpose: 'habitation',
          shape: 'cube',
          size_tier: 'medium',
          tech_level: 5,
          mass_kg: 1000,
          power_draw: 50,
          power_output: 0,
          crew_capacity: 0,
          description: '',
          is_custom: true,
          compatible_environments: ['vacuum', 'atmosphere'],
          tags: [],
          slot_size: '3x',
          base_stats: {}
        });
      }
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Авто-расчет характеристик на основе выбора
  const autoCalculate = () => {
    const baseMass = { 
      nano: 100, 
      micro: 500, 
      small: 1000, 
      medium: 5000, 
      large: 20000, 
      huge: 100000, 
      capital: 500000 
    };
    
    const basePower = { 
      habitation: 50, 
      energy: 10,
      energy_storage: 20,
      propulsion: 200, 
      weapon_system: 150, 
      shield_generator: 100,
      armor: 10,
      sensor: 30,
      life_support: 40,
      command: 60,
      cargo: 20,
      docking_bay: 100,
      research: 70,
      medical: 80,
      manufacturing: 200,
      mining: 150,
      agriculture: 30,
      communication: 40,
      navigation: 20,
      special: 100,
      composite: 150,
      nanotech: 200,
      repair: 50,
      structure: 10
    };
    
    const baseOutput = {
      energy: 300,
      energy_storage: 0
    };
    
    const massMult = baseMass[formData.size_tier] || 1000;
    const powerBase = basePower[formData.purpose] || 50;
    const outputBase = baseOutput[formData.purpose] || 0;
    const techMult = 1 + (formData.tech_level - 5) * 0.1;

    // Определяем размерный множитель для более реалистичных расчетов
    const sizeMultiplier = SIZES.find(s => s.value === formData.size_tier)?.multiplier || 1;

    setFormData(prev => ({
      ...prev,
      mass_kg: Math.round(massMult * (prev.purpose === 'energy' ? 1.5 : 1) * sizeMultiplier),
      power_draw: prev.purpose === 'energy' ? 10 : Math.round(Math.abs(powerBase) * techMult * sizeMultiplier),
      power_output: prev.purpose === 'energy' ? Math.round(outputBase * techMult * sizeMultiplier) : 
                    prev.purpose === 'energy_storage' ? 0 : 
                    prev.power_output,
      crew_capacity: prev.purpose === 'habitation' ? Math.round(4 * (massMult / 1000) * sizeMultiplier) : 
                    prev.purpose === 'command' ? Math.round(2 * (massMult / 1000) * sizeMultiplier) : 
                    prev.purpose === 'medical' ? Math.round(1 * (massMult / 1000) * sizeMultiplier) : 0
    }));
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Введите название модуля');
      return;
    }
    
    // Формируем данные в формате, совместимом с ModuleTemplates
    const moduleData = {
      id: initialData?.id || `module_${Date.now()}`,
      name: formData.name,
      purpose: formData.purpose,
      shape: formData.shape,
      base_shape: formData.shape,
      size_tier: formData.size_tier,
      tech_level: formData.tech_level,
      tech_level_min: formData.tech_level,
      description: formData.description,
      is_custom: formData.is_custom,
      compatible_environments: formData.compatible_environments,
      environment_required: formData.compatible_environments[0] || 'any',
      tags: formData.tags,
      slot_size: formData.slot_size,
      
      // Основные характеристики в base_stats
      base_stats: {
        mass_kg: formData.mass_kg,
        power_draw: formData.power_draw,
        power_output: formData.power_output,
        crew_capacity: formData.crew_capacity
      },
      
      // Для обратной совместимости
      mass_kg: formData.mass_kg,
      mass: formData.mass_kg,
      power_draw: formData.power_draw,
      power_output: formData.power_output,
      crew_capacity: formData.crew_capacity,
      
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };
    
    onSave(moduleData, saveToLibrary);
  };

  // Получаем выбранную категорию для отображения
  const selectedPurpose = PURPOSES.find(p => p.value === formData.purpose);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'create' ? 'Создание нового модуля' : 'Редактирование модуля'}
          <Chip 
            label={mode === 'create' ? 'Новый' : 'Редактирование'} 
            size="small" 
            color={mode === 'create' ? 'primary' : 'warning'}
          />
          {selectedPurpose && (
            <Chip 
              label={`${selectedPurpose.icon} ${selectedPurpose.category}`} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Фильтр для выбора из существующих модулей (только при создании) */}
          {mode === 'create' && availableModules && availableModules.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon /> Быстрый выбор из библиотеки
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Категория</InputLabel>
                      <Select
                        value={filterCategory}
                        label="Категория"
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        {CATEGORIES.map(cat => (
                          <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Поиск модуля..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      {filteredModules.length > 0 ? (
                        filteredModules.map(module => (
                          <Chip
                            key={module.id}
                            label={module.name}
                            onClick={() => {
                              const baseStats = module.base_stats || {};
                              setFormData({
                                ...formData,
                                name: module.name,
                                purpose: module.purpose,
                                shape: module.base_shape || module.shape || 'cube',
                                size_tier: module.size_tier || 'medium',
                                tech_level: module.tech_level_min || module.tech_level || 5,
                                mass_kg: baseStats.mass_kg || module.mass_kg || module.mass || 1000,
                                power_draw: baseStats.power_draw || module.power_draw || 50,
                                power_output: baseStats.power_output || module.power_output || 0,
                                crew_capacity: baseStats.crew_capacity || module.crew_capacity || 0,
                                description: module.description || '',
                                compatible_environments: module.compatible_environments || 
                                                       (module.environment_required ? [module.environment_required] : ['vacuum', 'atmosphere']),
                                tags: module.tags || [],
                                slot_size: module.slot_size || '3x'
                              });
                            }}
                            sx={{ m: 0.5 }}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          Нет модулей, соответствующих фильтру
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Основные параметры */}
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              label="Название модуля" 
              value={formData.name} 
              onChange={(e) => handleChange('name', e.target.value)} 
              margin="normal" 
              required 
              error={!formData.name.trim()}
              helperText={!formData.name.trim() ? 'Обязательное поле' : ''}
              placeholder="Например: Реактор класса X"
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Назначение</InputLabel>
              <Select 
                value={formData.purpose} 
                label="Назначение" 
                onChange={(e) => handleChange('purpose', e.target.value)}
                renderValue={(selected) => {
                  const purpose = PURPOSES.find(p => p.value === selected);
                  return `${purpose?.icon || '📦'} ${purpose?.label || selected}`;
                }}
              >
                {PURPOSES.map(p => (
                  <MenuItem key={p.value} value={p.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{p.icon}</span>
                      <span>{p.label}</span>
                      <Chip 
                        label={p.category} 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Геометрия</InputLabel>
              <Select 
                value={formData.shape} 
                label="Геометрия" 
                onChange={(e) => handleChange('shape', e.target.value)}
              >
                {SHAPES.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Размерный ряд</InputLabel>
              <Select 
                value={formData.size_tier} 
                label="Размерный ряд" 
                onChange={(e) => handleChange('size_tier', e.target.value)}
              >
                {SIZES.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Размер слота</InputLabel>
              <Select 
                value={formData.slot_size} 
                label="Размер слота" 
                onChange={(e) => handleChange('slot_size', e.target.value)}
              >
                <MenuItem value="1x">1x (Нано/Микро)</MenuItem>
                <MenuItem value="2x">2x (Малый)</MenuItem>
                <MenuItem value="3x">3x (Средний)</MenuItem>
                <MenuItem value="4x">4x (Большой)</MenuItem>
                <MenuItem value="5x">5x (Огромный)</MenuItem>
                <MenuItem value="6x">6x (Капитальный)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Совместимые среды</InputLabel>
              <Select
                multiple
                value={formData.compatible_environments}
                onChange={(e) => handleChange('compatible_environments', e.target.value)}
                label="Совместимые среды"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={ENVIRONMENTS.find(e => e.value === value)?.label || value} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
              >
                {ENVIRONMENTS.map(env => (
                  <MenuItem key={env.value} value={env.value}>
                    {env.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Характеристики */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Технические характеристики</Typography>
              
              <Typography variant="caption" display="block" gutterBottom>
                Технологический уровень: {formData.tech_level}
              </Typography>
              <Slider 
                value={formData.tech_level} 
                min={1} 
                max={10} 
                step={1} 
                marks={[
                  { value: 1, label: 'T1' }, 
                  { value: 5, label: 'T5' }, 
                  { value: 10, label: 'T10' }
                ]}
                onChange={(e, v) => handleChange('tech_level', v)} 
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
              
              <TextField 
                fullWidth 
                type="number" 
                label="Масса (кг)" 
                value={formData.mass_kg} 
                onChange={(e) => handleChange('mass_kg', parseInt(e.target.value) || 0)} 
                size="small" 
                sx={{ mb: 2 }} 
                InputProps={{ inputProps: { min: 0 } }}
              />
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField 
                    fullWidth 
                    type="number" 
                    label="Потребление (Вт)" 
                    value={formData.power_draw} 
                    onChange={(e) => handleChange('power_draw', parseInt(e.target.value) || 0)} 
                    size="small" 
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField 
                    fullWidth 
                    type="number" 
                    label="Выработка (Вт)" 
                    value={formData.power_output} 
                    onChange={(e) => handleChange('power_output', parseInt(e.target.value) || 0)} 
                    size="small" 
                    disabled={!['energy', 'energy_storage'].includes(formData.purpose)}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </Grid>
              
              <TextField 
                fullWidth 
                type="number" 
                label="Вместимость экипажа" 
                value={formData.crew_capacity} 
                onChange={(e) => handleChange('crew_capacity', parseInt(e.target.value) || 0)} 
                size="small" 
                InputProps={{ inputProps: { min: 0 } }}
              />

              <TextField 
                fullWidth 
                label="Теги" 
                value={formData.tags?.join(', ') || ''} 
                onChange={(e) => handleChange('tags', e.target.value.split(',').map(t => t.trim()).filter(t => t))} 
                size="small" 
                sx={{ mt: 2 }}
                placeholder="power, reactor, industrial (через запятую)"
                helperText="Ключевые слова для поиска и классификации"
              />
            </Box>
            
            <Button 
              variant="outlined" 
              startIcon={<MagicIcon />} 
              onClick={autoCalculate} 
              fullWidth 
              sx={{ mt: 2 }}
            >
              Авто-расчет баланса
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <TextField 
              fullWidth 
              multiline 
              rows={3} 
              label="Описание" 
              value={formData.description} 
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Введите описание модуля, его особенности и назначение..."
            />
          </Grid>

          {mode === 'create' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={saveToLibrary}
                    onChange={(e) => setSaveToLibrary(e.target.checked)}
                    color="primary"
                  />
                }
                label="Сохранить в библиотеку модулей"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Если отключить, модуль будет создан только для текущего объекта
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Отмена
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={<SaveIcon />} 
          color={mode === 'edit' ? 'warning' : 'primary'}
          disabled={!formData.name.trim()}
        >
          {mode === 'edit' ? 'Сохранить изменения' : 'Создать модуль'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}